import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from './config.js';
import GibsonLoadingLogo from './GibsonLoadingLogo.js';
import {
  Container,
  Typography,
  Grid,
  Box,
  Chip,
  Divider,
  Stack,
  IconButton,
  Breadcrumbs,
  Link,
  useTheme,
  useMediaQuery,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  ExpandMore as ExpandMoreIcon,
  FileDownload as FileDownloadIcon,
  PictureAsPdf as PictureAsPdfIcon,
  AddShoppingCart as AddShoppingCartIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import useAuditPageView from '../hooks/useAuditPageView';
import { getCurrentPriceList, getInventory, addQuotationItem, PRICE_CATEGORY_LABELS } from './quotationApi.js';

const ProductDetailPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { productId } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [exportSnackbar, setExportSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [thumbnailStartIndex, setThumbnailStartIndex] = useState(0);
  const THUMBNAILS_PER_PAGE = 5;

  // Pricing / stock / Add to Quotation
  const [priceList, setPriceList] = useState([]);
  const [inventory, setInventory] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [quoteQuantity, setQuoteQuantity] = useState(1);
  const [addingToQuotation, setAddingToQuotation] = useState(false);

  // product.images[] comes straight from the product-detail API - each entry
  // carries small_url/medium_url/original_url (locally cached, verified Oracle
  // images) and/or a legacy image_url. No separate Oracle fetch needed.
  const productImages = product?.images || [];
  const getMainImageUrl = (img) => img?.original_url || img?.medium_url || img?.small_url || img?.image_url;
  const getThumbnailUrl = (img) => img?.small_url || img?.medium_url || img?.image_url;

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("access"); // use access token
        if (!token) throw new Error("No access token found. Please login.");
        const response = await axios.get(`${API_BASE_URL}/api/product/${productId}/`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setProduct(response.data);
      } catch (error) {
        console.error('Error fetching product:', error);
        setProduct(null);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [productId]);

  useAuditPageView(product ? `Viewed product ${product.item_number} - ${product.name}` : null);

  useEffect(() => {
    if (!product?.item_number) return;
    let cancelled = false;

    const fetchPricingAndStock = async () => {
      setPricingLoading(true);
      try {
        const [prices, stock] = await Promise.all([
          getCurrentPriceList(product.item_number),
          getInventory(product.item_number),
        ]);
        if (cancelled) return;
        setPriceList(prices);
        setInventory(stock);
        setSelectedCategory((prev) => prev || (prices[0] ? prices[0].price_category : ''));
      } catch (error) {
        console.error('Error fetching pricing/inventory:', error);
        if (!cancelled) {
          setPriceList([]);
          setInventory(null);
        }
      } finally {
        if (!cancelled) setPricingLoading(false);
      }
    };
    fetchPricingAndStock();

    return () => { cancelled = true; };
  }, [product?.item_number]);

  const handleAddToQuotation = async () => {
    if (!product?.item_number || !selectedCategory) return;
    setAddingToQuotation(true);
    try {
      await addQuotationItem({
        sku: product.item_number,
        priceCategory: selectedCategory,
        quantity: quoteQuantity,
      });
      setExportSnackbar({ open: true, message: 'Added to your quotation.', severity: 'success' });
    } catch (err) {
      const message = err?.response?.data?.error || 'Could not add this item to your quotation.';
      setExportSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setAddingToQuotation(false);
    }
  };

  useEffect(() => {
    setCurrentImageIndex(0);
    setThumbnailStartIndex(0);
  }, [product?.item_number]);

  const handleNextImage = () => {
    if (productImages.length) {
      setCurrentImageIndex((prev) =>
        prev === productImages.length - 1 ? 0 : prev + 1
      );
    }
  };

  const handlePrevImage = () => {
    if (productImages.length) {
      setCurrentImageIndex((prev) =>
        prev === 0 ? productImages.length - 1 : prev - 1
      );
    }
  };

  // TEMP POC: keep the highlighted thumbnail within the visible paginated window.
  useEffect(() => {
    if (currentImageIndex < thumbnailStartIndex) {
      setThumbnailStartIndex(currentImageIndex);
    } else if (currentImageIndex >= thumbnailStartIndex + THUMBNAILS_PER_PAGE) {
      setThumbnailStartIndex(currentImageIndex - THUMBNAILS_PER_PAGE + 1);
    }
  }, [currentImageIndex]);

  const handleBackToSearch = () => navigate(-1);

  const handleExportToExcel = async () => {
    if (!product?.id) return;
    setExporting(true);
    try {
      const token = localStorage.getItem('access');
      const response = await fetch(`${API_BASE_URL}/api/export-products/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ product_ids: [product.id] }),
      });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const disposition = response.headers.get('content-disposition');
      const match = disposition?.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      link.download = match ? match[1].replace(/['"]/g, '') : `product_${product.item_number}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setExportSnackbar({ open: true, message: 'Product exported to Excel successfully.', severity: 'success' });
    } catch (err) {
      setExportSnackbar({ open: true, message: 'Export failed. Please try again.', severity: 'error' });
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!product?.id) return;
    setDownloadingPdf(true);
    try {
      const token = localStorage.getItem('access');
      const response = await fetch(`${API_BASE_URL}/api/product/${product.id}/download-pdf/`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('PDF download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `product_${product.item_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setExportSnackbar({ open: true, message: 'PDF downloaded successfully.', severity: 'success' });
    } catch (err) {
      setExportSnackbar({ open: true, message: 'PDF download failed. Please try again.', severity: 'error' });
    } finally {
      setDownloadingPdf(false);
    }
  };

  // Helper function to parse and display text fields with line breaks
  const renderTextWithLineBreaks = (text) => {
    if (!text) return null;
    return text.split('\n').map((line, index) => (
      <Typography key={index} variant="body2" paragraph={index < text.split('\n').length - 1}>
        {line}
      </Typography>
    ));
  };

  // Parse product features if it's a JSON string
  const parseFeatures = (features) => {
    if (!features) return [];
    try {
      // Check if it's a JSON array
      if (features.startsWith('[') && features.endsWith(']')) {
        return JSON.parse(features);
      }
      // If it's a string with bullet points
      return features.split('\n').filter(f => f.trim());
    } catch {
      // Return as array with one item if parsing fails
      return [features];
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="h6">Loading product...</Typography>
      </Container>
    );
  }

  if (!product) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="h6">Product not found</Typography>
        <Box mt={2}>
          <Link onClick={handleBackToSearch} sx={{ cursor: 'pointer', textDecoration: 'underline' }}>
            Back to Search
          </Link>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumbs + Export */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Breadcrumbs>
          <Link color="inherit" onClick={handleBackToSearch} sx={{ cursor: 'pointer' }}>
            Product Search
          </Link>
          <Typography color="text.primary">{product.name}</Typography>
        </Breadcrumbs>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={downloadingPdf ? <CircularProgress size={14} /> : <PictureAsPdfIcon />}
            onClick={handleDownloadPDF}
            disabled={downloadingPdf}
            sx={{ textTransform: 'none', borderRadius: '8px' }}
          >
            {downloadingPdf ? 'Generating...' : 'Sales Sheet'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={exporting ? <CircularProgress size={14} /> : <FileDownloadIcon />}
            onClick={handleExportToExcel}
            disabled={exporting}
            sx={{ textTransform: 'none', borderRadius: '8px' }}
          >
            {exporting ? 'Exporting...' : 'Export to Excel'}
          </Button>
        </Stack>
      </Box>

      {/* Main Grid */}
      <Grid
        container
        spacing={4}
        sx={{
          flexWrap: isMobile ? 'wrap' : 'nowrap',
        }}
      >
        {/* Left Column - Images */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            width: '200%',
            maxWidth: '600px',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {/* Main Image Container */}
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: isMobile ? 350 : 600,
              borderRadius: 3,
              backgroundColor: 'grey.100',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            {getMainImageUrl(productImages[currentImageIndex]) ? (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <img
                  src={getMainImageUrl(productImages[currentImageIndex])}
                  alt={product.name}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    transition: 'transform 0.3s ease-in-out',
                  }}
                />
              </Box>
            ) : (
              <Typography variant="body2">No Image Available</Typography>
            )}

            {/* Image navigation arrows */}
            {productImages.length > 1 && (
              <>
                <IconButton
                  onClick={handlePrevImage}
                  sx={{
                    position: 'absolute',
                    left: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    '&:hover': { backgroundColor: 'white' },
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
                <IconButton
                  onClick={handleNextImage}
                  sx={{
                    position: 'absolute',
                    right: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    '&:hover': { backgroundColor: 'white' },
                  }}
                >
                  <ArrowForwardIcon />
                </IconButton>

                {/* Image count chip */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 16,
                    left: '50%',
                    transform: 'translateX(-50%)',
                  }}
                >
                  <Chip
                    label={`${currentImageIndex + 1} / ${productImages.length}`}
                    size="small"
                    color="default"
                  />
                </Box>
              </>
            )}
          </Box>

          {/* Thumbnails */}
          {productImages.length > 1 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                size="small"
                onClick={() => setThumbnailStartIndex((prev) => Math.max(0, prev - 1))}
                disabled={thumbnailStartIndex === 0}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>

              <Box sx={{ display: 'flex', gap: 1.5, flex: 1, justifyContent: 'center' }}>
                {productImages
                  .slice(thumbnailStartIndex, thumbnailStartIndex + THUMBNAILS_PER_PAGE)
                  .map((img, i) => {
                    const index = thumbnailStartIndex + i;
                    const thumbUrl = getThumbnailUrl(img);
                    return (
                      <Box
                        key={index}
                        sx={{
                          width: 100,
                          height: 100,
                          borderRadius: 2,
                          overflow: 'hidden',
                          border: currentImageIndex === index ? 2 : 1,
                          borderColor:
                            currentImageIndex === index
                              ? 'primary.main'
                              : 'grey.300',
                          cursor: thumbUrl ? 'pointer' : 'default',
                          flexShrink: 0,
                          transition: 'border-color 0.3s ease',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'grey.100',
                        }}
                        onClick={() => thumbUrl && setCurrentImageIndex(index)}
                      >
                        {thumbUrl ? (
                          <img
                            src={thumbUrl}
                            alt={`Thumbnail ${index + 1}`}
                            loading="lazy"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                        ) : (
                          <GibsonLoadingLogo size={40} />
                        )}
                      </Box>
                    );
                  })}
              </Box>

              <IconButton
                size="small"
                onClick={() =>
                  setThumbnailStartIndex((prev) =>
                    Math.min(productImages.length - THUMBNAILS_PER_PAGE, prev + 1)
                  )
                }
                disabled={thumbnailStartIndex + THUMBNAILS_PER_PAGE >= productImages.length}
              >
                <ArrowForwardIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Grid>

        {/* Right Column - Product Details */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            pl: { md: 4 },
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            flex: 1,
          }}
        >
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            {product.productTitle || product.name}
          </Typography>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            by {product.brand}
          </Typography>

          {/* Collection and Category */}
          <Stack direction="row" spacing={2} sx={{ mt: 1, mb: 2 }}>
            {product.collectionName && (
              <Chip 
                label={`Collection: ${product.collectionName}`} 
                size="small" 
                color="primary" 
                variant="outlined"
              />
            )}
            {product.category && (
              <Chip 
                label={product.category} 
                size="small" 
                variant="outlined"
              />
            )}
          </Stack>

          <Divider sx={{ my: 3 }} />

          <Typography
            variant="h5"
            color="primary.main"
            fontWeight="bold"
            sx={{ mb: 3 }}
          >
            Item Number: {product.item_number}
          </Typography>

          {/* Pricing & Availability */}
          <Paper elevation={0} variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Pricing &amp; Availability
            </Typography>

            {pricingLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <>
                {priceList.length > 0 ? (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                    {priceList.map((row) => (
                      <Chip
                        key={row.price_category}
                        label={`${row.price_category_display}: $${Number(row.unit_price).toFixed(2)}`}
                        color={row.price_category === selectedCategory ? 'primary' : 'default'}
                        variant={row.price_category === selectedCategory ? 'filled' : 'outlined'}
                        onClick={() => setSelectedCategory(row.price_category)}
                        sx={{ mb: 1 }}
                      />
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    No current pricing available for this item.
                  </Typography>
                )}

                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                  <Chip
                    size="small"
                    label={`On Hand: ${inventory?.on_hand ?? 0}`}
                    color={inventory?.on_hand === 0 ? 'error' : 'default'}
                    variant="outlined"
                  />
                  <Chip
                    size="small"
                    label={`Available to Promise: ${inventory?.available_to_promise ?? 0}`}
                    variant="outlined"
                  />
                </Stack>

                {inventory?.on_order?.length > 0 && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                    On order: {inventory.on_order.map((row) => {
                      const [year, month] = row.expected_month.split('-');
                      const label = new Date(Number(year), Number(month) - 1, 1)
                        .toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
                      return `${label}: ${row.quantity}`;
                    }).join(', ')}
                  </Typography>
                )}

                {priceList.length > 0 && (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 220 }}>
                      <InputLabel id="price-category-label">Price Category</InputLabel>
                      <Select
                        labelId="price-category-label"
                        label="Price Category"
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                      >
                        {priceList.map((row) => (
                          <MenuItem key={row.price_category} value={row.price_category}>
                            {row.price_category_display || PRICE_CATEGORY_LABELS[row.price_category]}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      size="small"
                      type="number"
                      label="Qty"
                      value={quoteQuantity}
                      onChange={(e) => setQuoteQuantity(Math.max(1, parseInt(e.target.value, 10) || 1))}
                      sx={{ width: 90 }}
                      inputProps={{ min: 1 }}
                    />
                    <Button
                      variant="contained"
                      startIcon={addingToQuotation ? <CircularProgress size={14} color="inherit" /> : <AddShoppingCartIcon />}
                      onClick={handleAddToQuotation}
                      disabled={addingToQuotation}
                      sx={{ textTransform: 'none', borderRadius: '8px' }}
                    >
                      {addingToQuotation ? 'Adding...' : 'Add to Quotation'}
                    </Button>
                  </Stack>
                )}
              </>
            )}
          </Paper>

          {/* Romance Copy */}
          {product.romanceCopy && (
            <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50', mb: 3 }}>
              <Typography variant="body1" fontStyle="italic">
                "{product.romanceCopy}"
              </Typography>
            </Paper>
          )}

          {/* Long Description */}
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
            {product.longDescription}
          </Typography>

          {/* Product Details Accordion */}
          <Box sx={{ mb: 3 }}>
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6" fontWeight="bold">Product Details</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {product.material && (
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">Material</Typography>
                      <Typography variant="body2">{product.material}</Typography>
                    </Grid>
                  )}
                  {product.color && (
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">Color</Typography>
                      <Typography variant="body2">{product.color}</Typography>
                    </Grid>
                  )}
                  {product.packageType && (
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="text.secondary">Package Type</Typography>
                      <Typography variant="body2">{product.packageType}</Typography>
                    </Grid>
                  )}
                </Grid>
              </AccordionDetails>
            </Accordion>

            {/* Product Features */}
            {product.productFeatures && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6" fontWeight="bold">Features</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box component="ul" sx={{ pl: 2, m: 0 }}>
                    {parseFeatures(product.productFeatures).map((feature, index) => (
                      <Typography component="li" key={index} variant="body2" sx={{ mb: 1 }}>
                        {feature}
                      </Typography>
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}

            {/* What's in the Box */}
            {product.whatIsInTheBox && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6" fontWeight="bold">What's in the Box</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {renderTextWithLineBreaks(product.whatIsInTheBox)}
                </AccordionDetails>
              </Accordion>
            )}

            {/* Care Instructions */}
            {product.careInstructions && (
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6" fontWeight="bold">Care Instructions</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {renderTextWithLineBreaks(product.careInstructions)}
                </AccordionDetails>
              </Accordion>
            )}

            {/* Master Carton Details */}
            {product.cartonDetails?.length > 0 && (
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6" fontWeight="bold">
                    Master Carton Details
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={1}>
                    {product.cartonDetails.map((detail, index) => (
                      <Typography key={index} variant="body2">
                        • {detail.replace(/^Weight:/, 'MC Weight:')}
                      </Typography>
                    ))}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Keywords/Tags */}
            {product.keywords && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Keywords
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {product.keywords.split(',').map((keyword, index) => (
                    <Chip
                      key={index}
                      label={keyword.trim()}
                      size="small"
                      variant="outlined"
                      sx={{ mb: 1 }}
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>

      <Snackbar
        open={exportSnackbar.open}
        autoHideDuration={4000}
        onClose={() => setExportSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={exportSnackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {exportSnackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ProductDetailPage;